import { useNavigate } from "react-router-dom";
import { useEmbedMode } from "../layouts/EmbedLayout";

// Tenant-aware navigation helper. Same surface as react-router's
// `useNavigate` for the common (string-path, options) case, but
// transparently rewrites the destination when rendered inside an
// EmbedLayout tree so internal links stay under `/embed/*`.
//
// Iframe Reuse Refactor v3.5 (28-04-2026): /embed/* now mirrors
// the tenant route tree 1:1 (App.js mounts <IncomeTaxLayout>,
// <GSTLayout>, <TDSLayout> and their children at /embed/income-tax,
// /embed/gstin, /embed/tds). So this hook degrades to a pure
// prefix-prepend — no per-pathname mapping table needed anymore. The
// older `TENANT_TO_EMBED_PATHNAME` lookup (which mapped
// `/income-tax → /embed/notices/income-tax` for the bespoke
// EmbedNotices page) is gone with the page itself.
//
// Outside the embed tree, `useEmbedMode()` returns the default
// `{ isEmbed: false }`, so the hook is a no-op pass-through to
// `react-router-dom`. Tenant users see zero behaviour change.
//
// Spec: IFRAME_REUSE_IMPLEMENTATION_GUIDE.md §8.

const useTenantNavigate = () => {
  const navigate = useNavigate();
  const { isEmbed } = useEmbedMode();

  return (path, options) => {
    // Outside an embed tree → pure pass-through. Critical: tenant
    // users at /dashboard must keep landing on /income-tax (not
    // /embed/income-tax) when they click the same card.
    if (!isEmbed) return navigate(path, options);

    // Non-string inputs (numbers like navigate(-1) for back-nav,
    // partial location objects) have no pathname to map — pass
    // through unchanged.
    if (typeof path !== "string") return navigate(path, options);

    // Already an /embed path → don't double-prefix.
    if (path.startsWith("/embed")) return navigate(path, options);

    // Relative paths (no leading slash) like "clients?type=active"
    // are resolved by react-router against the current route — we
    // must NOT prefix those, otherwise "clients?type=active" inside
    // /embed/income-tax becomes "/embedclients?..." (broken).
    if (!path.startsWith("/")) return navigate(path, options);

    // Absolute tenant path → prepend /embed. Querystring + hash
    // ride along untouched because we're doing a string concat on
    // the full input, not just the pathname.
    return navigate(`/embed${path}`, options);
  };
};

export default useTenantNavigate;
